import { Demand, DemandLevel, DemandStatus, Law, Origin, Paragraph } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { UseFormReset } from 'react-hook-form'
import { NavigateFunction } from 'react-router-dom'
import React from 'react'
import { setLoading, UpdateItemInCollection } from './stateHelper'
import { t } from '../i18n/i18n'
import { api } from './apiHelper'

export enum SelectedDemandModal {
   ARCHIVE,
   DELETE,
   UNDO,
   PARENT,
}

export const deleteUnselectedParagraphDemands = async (
   paragraphs: Paragraph[],
   SelectedParagraphs: (Demand & {
      paragraphId: number
   })[],
   law: Law,
   accessToken: string
): Promise<Paragraph[]> => {
   try {
      if (
         JSON.stringify(
            paragraphs
               .filter((x) => x.demandId)
               .map((x) => x.demandId)
               .sort()
         ) !==
         JSON.stringify(
            SelectedParagraphs.filter((x) => x.id)
               .map((x) => x.id)
               .sort()
         )
      ) {
         const paragraphsToDelete = paragraphs.filter((paragraph) => {
            return paragraph.demandId && !SelectedParagraphs.some((selectedParagraph) => selectedParagraph.paragraphId === paragraph.id)
         })
         await Promise.all(
            paragraphsToDelete.map(async (paragraph) => {
               await api(accessToken).demandDelete(paragraph.demandId)
               paragraphs.find((x) => x.id === paragraph.id).demandId = null
               paragraphs.find((x) => x.id === paragraph.id).law = law
               api(accessToken).updateParagraph(paragraphs.find((x) => x.id === paragraph.id))
            })
         )
      }
      return paragraphs
   } catch (error) {}
}

export const deleteDemands = async (demandIds: string[], accessToken: string): Promise<void> => {
   await Promise.all(
      demandIds.map(async (id) => {
         await api(accessToken).demandDelete(id)
      })
   )
}
export const statusBadgeColor = (lvl: number): 'brand' | 'success' | 'warning' | 'danger' => {
   switch (lvl) {
      case 0:
         return 'success'
      case 1:
         return 'warning'
      default:
         break
   }
}
export const getNewDemand = (parentDemand: Demand, modifiedBy: string): Demand => ({
   area: parentDemand?.area,
   level: parentDemand?.level + 1,
   modifiedBy: modifiedBy,
   origin: Origin.Sweden,
   parentId: parentDemand?.id,
   source: '',
   type: parentDemand?.type,
   subType: null,
   text: '',
   url: '',
   urlText: '',
   designation: '',
})

export const updateDemand = async (
   demandToUpdate: Demand,
   dataContext: IDataContext,
   reset: UseFormReset<Demand>,
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   setOriginDemand: React.Dispatch<React.SetStateAction<Demand>>,
   setBread: (parentId: string) => Promise<void>
) => {
   try {
      const { accessToken } = dataContext.state
      const updatedDemand: Demand = (await api(accessToken).demandUpdate(demandToUpdate)).data

      const demands: Demand[] = (await api(accessToken).demandList()).data

      dataContext.setRootState((prev) => ({ ...prev, demands }))
      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')

      reset(updatedDemand)
      setOriginDemand(updatedDemand)
      await setBread(updatedDemand.parentId)
      return updatedDemand
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const hasActiveChildDemands = (demand: Demand): boolean => {
   if (!demand.children) {
      return false
   }
   return demand.children.some((x) => x.status !== DemandStatus.Archived)
}
export const hasArchivedChildDemands = (demand: Demand): boolean => {
   if (!demand.children) {
      return false
   }

   return demand.children.some((x) => x.status === DemandStatus.Archived)
}
export const hasQuestions = (demand: Demand): boolean => {
   return demand.questions && demand.questions.length > 0
}

const updateParentDemand = async (updatedDemands: Demand[], demandsToMove: Demand[], targetDemand: Demand) => {
   demandsToMove.forEach((demand) => {
      updatedDemands.push({
         ...demand,
         parentId: targetDemand.id,
      })
   })
}

export const onDemandMove = async (dataContext: IDataContext, targetDemand: Demand, onModalClose: () => void) => {
   const { selectedDemands, accessToken } = dataContext.state
   const lvlTwoDemands = selectedDemands.filter((x) => x.level === DemandLevel.Two)
   const lvlThreeDemands = selectedDemands.filter((x) => x.level === DemandLevel.Three)
   let updatedDemands: Demand[] = []
   setLoading(dataContext.setRootState, true)
   try {
      await updateParentDemand(updatedDemands, targetDemand.level === DemandLevel.One ? lvlTwoDemands : lvlThreeDemands, targetDemand)
      await api(accessToken).demandBatchUpdate(updatedDemands)
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('DemandMoveError'), error.message)
   } finally {
      const demands = (await api(accessToken).demandList()).data

      dataContext.setRootState((prev) => ({
         ...prev,
         isLoading: false,
         moveDemands: false,
         selectedDemands: [],
         demands: demands,
      }))
      onModalClose()
   }
}

export const archiveDemand = async (
   demand: Demand,
   dataContext: IDataContext,
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   modifiedBy: string,
   grandParentId: string,
   setOpenModal: React.Dispatch<React.SetStateAction<SelectedDemandModal>>
) => {
   setIsSubmitting(true)
   try {
      const { demands, accessToken } = dataContext.state
      demand.modifiedBy = modifiedBy
      const updatedDemands: Demand[] = (await api(accessToken).demandArchiveUpdate(demand)).data

      const grandParent = demands.find((d) => d.id === grandParentId)
      if (demand.level === 2) {
         grandParent.children = updatedDemands.length > 0 ? updatedDemands.sort((a: any, b: any) => a.order - b.order) : []
      }
      if (demand.level === 3) {
         const parent = grandParent.children.find((y) => y.id === demand.parentId)

         parent.children = updatedDemands.length > 0 ? updatedDemands.sort((a: any, b: any) => a.order - b.order) : []
      }

      UpdateItemInCollection(dataContext.setRootState, demands, grandParent, 'demands')
      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')
      setOpenModal(null)
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const deleteDemandAndResetLaw = async (
   demand: Demand,
   dataContext: IDataContext,
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   modifiedBy: string,
   grandParentId: string,
   navigate: NavigateFunction
) => {
   setIsSubmitting(true)
   try {
      const { demands, accessToken } = dataContext.state
      const updatedDemands = (await api(accessToken).demandResetlawDelete(demand.id, modifiedBy)).data
      const grandParent = demands.find((d) => d.id === grandParentId)

      if (demand.status !== DemandStatus.Archived) {
         if (demand.level === DemandLevel.Two) {
            grandParent.children = updatedDemands.sort((a: any, b: any) => a.order - b.order)
         }
         if (demand.level === DemandLevel.Three) {
            const parent = grandParent.children.find((y) => y.id === demand.parentId)
            parent.children = updatedDemands.sort((a: any, b: any) => a.order - b.order)
         }
      }

      UpdateItemInCollection(dataContext.setRootState, demands, grandParent, 'demands')

      dataContext.handleMessage(dataContext.setRootState, 'success', t('ItemDeleted'), '')

      navigate('/demands')
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('DeleteError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const addDemand = async (
   demand: Demand,
   grandParentId: string,
   dataContext: IDataContext,
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   navigate: NavigateFunction
) => {
   try {
      const { demands, accessToken } = dataContext.state
      const newDemand: Demand = (await api(accessToken).demandCreate(demand)).data
      let grandParent: Demand

      if (demand.level === DemandLevel.Two) {
         grandParent = demands.find((x) => x.id === demand.parentId)

         grandParent.children.push(newDemand)
         grandParent.children = grandParent.children.sort((a: any, b: any) => a.order - b.order)
      }
      if (demand.level === DemandLevel.Three) {
         grandParent = demands.find((x) => x.id === grandParentId)
         const parent = grandParent.children.find((y) => y.id === newDemand.parentId)

         parent.children.push(newDemand)
         parent.children = parent.children.sort((a: any, b: any) => a.order - b.order)
      }
      UpdateItemInCollection(dataContext.setRootState, demands, grandParent, 'demands')

      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')
      navigate(`/demands/${newDemand.id}`)
      return newDemand
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const addDemandFromList = async (demand: Demand, demandsToUpdate: Demand[], dataContext: IDataContext, navigate: NavigateFunction) => {
   const { accessToken } = dataContext.state
   try {
      const newDemand: Demand = (await api(accessToken).demandCreate(demand)).data
      await api(accessToken).demandBatchUpdate(demandsToUpdate)

      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')

      navigate(`/demands/${newDemand.id}`)
      return newDemand
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      const demands = (await api(accessToken).demandList()).data
      dataContext.setRootState((prev) => ({ ...prev, demands }))
   }
}

export const demandSelectIsDisabled = (selectedDemands: Demand[], demand: Demand) => {
   const lvlOneHasNoChildren = !hasActiveChildDemands(demand) && demand.level === DemandLevel.One
   const lvlThreeParentIsSelected = demand.level === DemandLevel.Three && selectedDemands.some((x) => x.id === demand.parentId)
   const lvlThreeWithoutParentIsDisabled = demand.level === DemandLevel.Three && selectedDemands.some((x) => x.level === DemandLevel.Two)
   const lvlThreeValuesWithoutParent =
      demand.level === DemandLevel.Two &&
      selectedDemands.filter((x) => x.level === DemandLevel.Two).length < 1 &&
      selectedDemands.some((x) => x.level === DemandLevel.Three)
   if (lvlOneHasNoChildren) {
      return lvlOneHasNoChildren
   }
   if (lvlThreeParentIsSelected) {
      return lvlThreeParentIsSelected
   }
   if (lvlThreeWithoutParentIsDisabled) {
      return lvlThreeWithoutParentIsDisabled
   }
   if (lvlThreeValuesWithoutParent) {
      return lvlThreeValuesWithoutParent
   } else {
      return false
   }
}

export const onDemandSelect = async (
   selectedDemands: Demand[],
   selectedDemand: Demand,
   expandedDemands: string[],
   checked: boolean,
   dataContext: IDataContext,
   toggleRowExpansion: (demandId: string) => void
) => {
   let movableItems: Demand[] = selectedDemands

   const expandAndSelect = (demand: Demand) => {
      movableItems.push(selectedDemand)
      const exists = (demandId: string) => {
         return movableItems.some((x) => x.id === demandId)
      }
      demand.children.forEach((child) => {
         if (!exists(child.id)) {
            movableItems.push(child)
            if (child?.children?.length > 0) {
               child?.children.forEach((grandChild) => {
                  if (!exists(grandChild.id)) {
                     movableItems.push(grandChild)
                  }
               })
            }
         }
      })
   }

   if (checked) {
      expandAndSelect(selectedDemand)

      const hasChildren = (demand: Demand) => {
         return demand?.children?.length > 0
      }
      const isExpanded = (demandId: string) => {
         return expandedDemands.some((demand) => demand === demandId)
      }
      if (!isExpanded(selectedDemand.id)) {
         toggleRowExpansion(selectedDemand.id)
      }

      if (hasChildren(selectedDemand)) {
         // eslint-disable-next-line array-callback-return
         selectedDemand.children.map((child) => {
            if (!isExpanded(child.id)) {
               toggleRowExpansion(child.id)
            }
         })
      }
   } else {
      movableItems = movableItems.filter((x) => x.id !== selectedDemand.id)
      const lvlThreeWithoutParentId = movableItems.filter((x) => x.parentId === selectedDemand.id && x.level === DemandLevel.Three)
      const selectedLvlTwos = movableItems.find((x) => x.level === DemandLevel.Two)

      if (selectedLvlTwos && lvlThreeWithoutParentId) {
         lvlThreeWithoutParentId.forEach((lvlThree) => {
            movableItems = movableItems.filter((x) => x.id !== lvlThree.id)
         })
      }
   }
   dataContext.setRootState((prev) => ({
      ...prev,
      selectedDemands: movableItems,
   }))
}

export const onDemandMoveClick = (dataContext: IDataContext) => {
   dataContext.setRootState((prev) => ({
      ...prev,
      moveDemands: !prev.moveDemands,
      selectedDemands: dataContext.state.moveDemands ? [] : prev.selectedDemands,
   }))
}

export const levelBadgeColor = (lvl: DemandLevel, classes: any) => {
   switch (lvl) {
      case DemandLevel.One:
         return classes.brand
      case DemandLevel.Two:
         return classes.gold
      case DemandLevel.Three:
         return classes.informative
   }
}

export const isDemandFormDisabled = (selectedDemand: Demand) => {
   return selectedDemand && selectedDemand.status === DemandStatus.Archived
}
